import type { NavButtonFontSchemaType, NavButtonSchemaType } from '@vedalib/editor/lib/brand'
import { ButtonNavigationAction } from '@vedalib/editor/lib/elements'
import React, { useCallback } from 'react'

import { getButtonLabel } from 'components/Editor/EditorElements/elements/Button/helper'
import type { ElementFontCss, ElementStyleCss } from 'services/Branding/types'
import { useProjectT } from 'services/ProjectLabels'
import { showIncompleteElementList, showNotCompletedSections } from 'services/Store/Project/actions'
import { PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getProject } from 'services/Store/Project/selectors'
import { getNextSectionTrigger, getPrevSectionTrigger } from 'services/Store/Project/triggers'
import type { IBlockMode } from 'services/Store/Project/types'
import { t } from 'services/Translation'

import * as s from './LandingFooterNavigation.module.scss'
import NavigationButtonsDemo from './NavigationButtonsDemo'
import BaseNavigationButton from './components/BaseNavigationButton'
import ButtonIcon from './components/ButtonIcon'
import { getIconUrl } from './helpers'

interface ILandingFooterNavigationProps {
  mode: IBlockMode
  styles: ElementStyleCss<NavButtonSchemaType>
  fonts?: ElementFontCss<NavButtonFontSchemaType>
}

const LandingFooterNavigation: React.FC<ILandingFooterNavigationProps> = ({
  mode,
  styles,
  fonts,
}) => {
  const pt = useProjectT()
  const dispatch = useProjectDispatch()
  const prevTrigger = useProjectContext(getPrevSectionTrigger)
  const nextTrigger = useProjectContext(getNextSectionTrigger)
  const showIncompleteModal = useProjectContext(getProject)?.displayIncompleteModal

  const nextLabel = getButtonLabel(
    {
      'project/setCourseNavigation': pt('navigationButton.continue'),
      'project/courseEnd': pt('navigationButton.end'),
      noop: pt('navigationButton.continue'),
    },
    nextTrigger?.action,
  )
  const isDemoMode = mode.previewMode === PreviewMode.demo

  const handlePrevTrigger = useCallback(
    () => !prevTrigger.disabled && dispatch(prevTrigger.actionCreator()),
    [dispatch, prevTrigger],
  )

  const handleNextTrigger = useCallback(() => {
    if (nextTrigger.disabled && showIncompleteModal) {
      if (nextTrigger.action === 'project/courseEnd') {
        dispatch(showNotCompletedSections())
      } else {
        dispatch(showIncompleteElementList())
      }
    } else {
      dispatch(nextTrigger.actionCreator())
    }
  }, [dispatch, nextTrigger, showIncompleteModal])

  const disabledPrev = { ...styles?.prevButton__var, ...styles?.prevButtonDisabled__var }
  const disabledNext = { ...styles?.nextButton__var, ...styles?.nextButtonDisabled__var }

  const showButtons = styles.show.show

  const nextIcon = getIconUrl(styles?.nextButton__var)
  const prevIcon = getIconUrl(styles?.prevButton__var)

  // Return null when not in demo mode and buttons are hidden
  if (!showButtons && !isDemoMode) {
    return null
  }

  return isDemoMode ? (
    <NavigationButtonsDemo fonts={fonts} mode={mode} styles={styles} />
  ) : (
    <div className={s.root} style={{ gap: styles.padding__var.gap }}>
      {showButtons && (
        <>
          {(!prevTrigger.disabled || isDemoMode) && (
            <BaseNavigationButton
              action={ButtonNavigationAction.prevSection}
              disabled={prevTrigger.disabled}
              fonts={fonts}
              icon={<ButtonIcon defaultName='courseArrowBack' url={prevIcon} />}
              iconPosition='left'
              label={pt('navigationButton.return')}
              mode={mode}
              onClick={handlePrevTrigger}
              styles={{
                root: styles?.root,
                button: prevTrigger.disabled ? disabledPrev : styles.prevButton__var,
                hover: styles.hover,
                effects: styles.effects,
              }}
            />
          )}
          <BaseNavigationButton
            action={ButtonNavigationAction.nextSection}
            disabled={!showIncompleteModal && nextTrigger.disabled}
            fonts={fonts}
            icon={
              nextTrigger.action === 'project/setCourseNavigation' ? (
                <ButtonIcon defaultName='courseArrowForward' url={nextIcon} />
              ) : null
            }
            iconPosition='right'
            label={isDemoMode ? t('elements.button.demo.value.continue') : nextLabel}
            mode={mode}
            onClick={handleNextTrigger}
            styles={{
              root: styles.root,
              button:
                nextTrigger.disabled && !showIncompleteModal
                  ? disabledNext
                  : styles.nextButton__var,
              hover: styles.hover,
              effects: styles.effects,
            }}
          />
        </>
      )}
    </div>
  )
}

export default LandingFooterNavigation
